import React, { useState, useEffect } from 'react';
import { Card, Button, Empty, Tabs, Badge, message, Modal, Space, Typography, Row, Col } from 'antd';
import { HeartFilled, HeartOutlined, ShoppingCartOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useWishlist } from '../../contexts/WishlistContext';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import LoadingSkeleton from '../UI/LoadingSkeleton';
import SocialShare from '../UI/SocialShare';
import VendorCard from '../UI/VendorCard';
import MenuCard from '../UI/MenuCard';
import FoodCard from '../UI/FoodCard';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function Wishlist() {
    const { 
        wishlistItems, 
        loading, 
        removeFromWishlist, 
        clearWishlist, 
        getWishlistByType 
    } = useWishlist();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');

    const vendors = getWishlistByType('vendor');
    const dishes = getWishlistByType('menu');

    const handleRemoveItem = async (item) => {
        await removeFromWishlist(item.id, item.type);
    };

    const handleClearWishlist = () => {
        Modal.confirm({
            title: 'Clear Wishlist',
            content: 'Are you sure you want to remove all items from your wishlist?',
            okText: 'Yes, Clear All',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: clearWishlist,
        });
    };

    const handleAddToCart = async (item) => {
        if (item.type === 'menu') {
            await addToCart(item.data, 1);
            message.success('Added to cart!');
        } else {
            message.info('Please visit the vendor page to add items to cart');
        }
    };

    const handleViewItem = (item) => {
        if (item.type === 'vendor') {
            navigate(`/vendors/${item.id}`);
        } else {
            navigate(`/menu?dish=${item.id}`);
        }
    };

    const renderVendorCard = (vendor) => (
        <VendorCard
            key={`vendor_${vendor.id}`}
            vendor={vendor.data}
            onView={(v) => navigate(`/vendors/${v.id}`)}
            extraTopRight={
                <Button
                    type="text"
                    icon={<HeartFilled />}
                    onClick={(e) => { e.stopPropagation(); handleRemoveItem(vendor); }}
                    className="text-red-500 hover:text-red-600"
                    size="small"
                />
            }
        />
    );

    const renderDishCard = (dish) => (
        <FoodCard
            key={`menu_${dish.id}`}
            variant="menu"
            item={dish.data}
            onView={(item) => navigate(`/menu?dish=${item.id}`)}
            onAddToCart={() => handleAddToCart(dish)}
            onFavorite={() => handleRemoveItem(dish)}
            isFavorite={true}
            actionsPlacement="footer"
        />
    );

    const renderEmptyState = (type) => (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                <div className="text-center">
                    <Title level={4}>No {type} in your wishlist</Title>
                    <Paragraph type="secondary">
                        {type === 'vendors' 
                            ? 'Start exploring vendors and add them to your wishlist'
                            : 'Browse our menu and save your favorite dishes'
                        }
                    </Paragraph>
                    <Button 
                        type="primary" 
                        onClick={() => navigate(type === 'vendors' ? '/vendors' : '/menu')}
                    >
                        Browse {type === 'vendors' ? 'Vendors' : 'Menu'}
                    </Button>
                </div>
            }
        />
    );

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <LoadingSkeleton.VendorCardSkeleton key={index} />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
                <Title level={2} className="flex items-center gap-2">
                    <HeartFilled className="text-red-500" />
                    My Wishlist
                    <Badge count={wishlistItems.length} showZero />
                </Title>
                <Paragraph type="secondary">
                    Your saved vendors and favorite dishes
                </Paragraph>
            </div>

            {wishlistItems.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div className="text-center">
                            <Title level={4}>Your wishlist is empty</Title>
                            <Paragraph type="secondary">
                                Start exploring and add items you love to your wishlist
                            </Paragraph>
                            <Space>
                                <Button 
                                    type="primary" 
                                    onClick={() => navigate('/vendors')}
                                >
                                    Browse Vendors
                                </Button>
                                <Button onClick={() => navigate('/menu')}>
                                    Browse Menu
                                </Button>
                            </Space>
                        </div>
                    }
                />
            ) : (
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    tabBarExtraContent={
                        <Button 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={handleClearWishlist}
                        >
                            Clear All
                        </Button>
                    }
                >
                    <TabPane 
                        tab={`All (${wishlistItems.length})`} 
                        key="all"
                    >
                        <Row gutter={[16, 16]}>
                            {vendors.map(vendor => (
                                <Col xs={24} sm={12} md={8} lg={6} key={`vendor_${vendor.id}`}>
                                    {renderVendorCard(vendor)}
                                </Col>
                            ))}
                            {dishes.map(dish => (
                                <Col xs={24} sm={12} md={8} lg={6} key={`menu_${dish.id}`}>
                                    {renderDishCard(dish)}
                                </Col>
                            ))}
                        </Row>
                    </TabPane>
                    
                    <TabPane 
                        tab={`Vendors (${vendors.length})`} 
                        key="vendors"
                    >
                        {vendors.length === 0 ? (
                            renderEmptyState('vendors')
                        ) : (
                            <Row gutter={[16, 16]}>
                                {vendors.map(vendor => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={`vendor_${vendor.id}`}>
                                        {renderVendorCard(vendor)}
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </TabPane>
                    
                    <TabPane 
                        tab={`Dishes (${dishes.length})`} 
                        key="dishes"
                    >
                        {dishes.length === 0 ? (
                            renderEmptyState('dishes')
                        ) : (
                            <Row gutter={[16, 16]}>
                                {dishes.map(dish => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={`menu_${dish.id}`}>
                                        {renderDishCard(dish)}
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </TabPane>
                </Tabs>
            )}
        </div>
    );
}

export default Wishlist;
